import React, { useContext, useEffect, useState } from 'react';
import { Layout, Spin } from 'antd';
import styles from './Page.style.js';
import PageHeader from './PageHeader';
import { layout } from '../../styles/theme';
import { ThemeContext } from '../contexts/Theme.context';
import Head from 'next/head';
import ScrollDown from '../../components/ScrollDown/ScrollDown';

const { Header, Content, Footer } = Layout;

export default function Page({
  children,
  footer,
  backButton,
  headerLeftExtra,
  showLanguageSelector,
  showLogoutButton = true,
  onLongPress,
  showShareButton,
  kioskDeviceUuid,
}) {
  const { state: theme } = useContext(ThemeContext);
  const customCss = theme && theme.customCss ? theme.customCss : '';
  const themeStyle = styles(theme);

  const [title, setTitle] = useState('Check-in');

  useEffect(() => {
    setTitle(window.location.hostname?.includes('checkout') ? 'Check-out' : 'Check-in');
  }, []);

  useEffect(() => {
    const isTravelAgentProtectedRoute = () => {
      const pathname = window.location.pathname || '';
      const isBookingSelectorPath = pathname.includes('/bookingSelector');

      let isGroupCheckinSource = false;
      try {
        const queryParams = new URLSearchParams(window.location.search || '');
        isGroupCheckinSource = queryParams.get('source') === 'group-checkin';
      } catch (e) {}

      return isBookingSelectorPath || isGroupCheckinSource;
    };

    const checkLoggedOut = () => {
      const isLoggedOut = sessionStorage.getItem('logged-out');
      const isLoginRoute = window.location.pathname?.startsWith('/login');
      const isProtectedRoute = isTravelAgentProtectedRoute();

      if (isLoggedOut && !isLoginRoute && isProtectedRoute) {
        window.location.replace('/login');
      }
    };

    checkLoggedOut();
    window.addEventListener('pageshow', checkLoggedOut);
    window.addEventListener('popstate', checkLoggedOut);
    return () => {
      window.removeEventListener('pageshow', checkLoggedOut);
      window.removeEventListener('popstate', checkLoggedOut);
    };
  }, []);

  const userType = localStorage.getItem('kiosk-user-type');
  const isTravelAgentUser = userType === 'travel_agent' ? true : undefined;

  const showLanguageSelectorCalc = () => {
    if (!theme.isKiosk && showLanguageSelector) {
      return true;
    } else if (isTravelAgentUser && showLanguageSelector) {
      return true;
    } else { 
      return showLanguageSelector; 
    }
  };

  return theme ? (
    <>
      {kioskDeviceUuid && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            paddingTop: '8px',
            paddingRight: '24px',
          }}
        >
          <span>{'Terminal: '}</span>
          <b>{`${kioskDeviceUuid}`}</b>
        </div>
      )}
      <Head>
        {theme.customLink ? <link href={theme.customLink} rel="stylesheet" /> : null}
        <title>
          {theme.name} | {title}
        </title>
        <link rel="icon" href={theme.logo} />
      </Head>
      <Layout className={'layout'}>
        <Header
          className={`header ${theme.isIframe && theme.canUseIframe ? 'on-iframe-header' : ''}`}
        >
          <PageHeader
            backButton={backButton}
            headerLeftExtra={headerLeftExtra}
            showLanguageSelector={showLanguageSelectorCalc()}
            showLogoutButton={showLogoutButton}
            onLongPress={onLongPress}
            showShareButton={showShareButton}
          ></PageHeader>
        </Header>
        <Content
          className={'content'}
          style={{ marginBottom: footer && theme.isKiosk ? layout.marginWithFooter : 0, position: 'relative' }}
        >
          {children}
          {!theme.isKiosk && footer ? <div className={'form-footer footer'}>{footer}</div> : null}
        </Content>
        <ScrollDown></ScrollDown>
        {theme.isKiosk && footer ? <Footer className={'footer'}>{footer}</Footer> : null}
      </Layout>
      <style jsx>{themeStyle}</style>
      <style jsx global>{`
        html,
        body {
          height: 100%;
        }

        body {
          margin: 0;
          ${customCss}
        }

        #__next {
          min-height: 100%;
          background: ${theme.bgColor};
          display: flex;
          flex-direction: column;
        }

        .layout.ant-layout {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .content.ant-layout-content {
          ${theme?.isKiosk ? 'flex: 1;' : ''}
        }

        .footer.ant-layout-footer {
          background: transparent;
        }

        @media (min-width: 1024px) {
          .layout.ant-layout {
            max-width: calc(${theme?.isKiosk ? '80vw' : '50vw'} + 40px);
            margin: 0 auto;
          }
        }
      `}</style>
    </>
  ) : (
    <div className={'empty-page'}>
      <Spin></Spin>
      <style jsx global>{`
        .empty-page {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
        }
        .ant-spin-dot-item {
          background-color: #ccc;
        }
      `}</style>
    </div>
  );
}
